import { useState, useEffect, useCallback } from 'react';
import { getPollingConfig, updatePollingConfig, triggerPoll } from '@/services/api';
import type { PollingConfig } from '@/types/tenant';

export function usePollingStatus() {
  const [config, setConfig] = useState<PollingConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPollingConfig();
      setConfig(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch polling config'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const updateConfig = useCallback(
    async (
      updates: Partial<Pick<PollingConfig, 'intervalSeconds' | 'isActive' | 'resourceCautionThreshold' | 'resourceCriticalThreshold'>> & {
        pollingFromDate?: string | null;
        pollingToDate?: string | null;
        targetVersion?: string | null;
      }
    ) => {
      try {
        const updated = await updatePollingConfig(updates);
        setConfig(updated);
        return updated;
      } catch (err) {
        throw err instanceof Error ? err : new Error('Failed to update polling config');
      }
    },
    []
  );

  const trigger = useCallback(async () => {
    try {
      await triggerPoll();
      // Refresh config after triggering
      await fetchConfig();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to trigger poll');
    }
  }, [fetchConfig]);

  return { config, loading, error, updateConfig, trigger, refetch: fetchConfig };
}
